import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import useErrorDialog from "@src/components/apollo/errorprovider/useErrorDialog";
import { ErrorScope, errorScopeForOperationContext } from "@src/components/apollo/ErrorScope";
import config from "@src/config";
import fragmentTypes from "@src/fragmentTypes.json";
import { UserContext } from "@vivid/react-oidc-client";
import { type FunctionComponent, type PropsWithChildren, useContext } from "react";

const CustomApolloProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const userContext = useContext(UserContext);
    const errorDialog = useErrorDialog();

    const link = ApolloLink.from([
        onError(({ graphQLErrors, networkError, operation }) => {
            const errorScope = errorScopeForOperationContext(operation.getContext());

            if (graphQLErrors) {
                graphQLErrors.forEach(({ extensions, message }) => {
                    if (extensions?.code === "UNAUTHENTICATED" || message === "AccessTokenInvalid") {
                        userContext.signOut();
                    } else {
                        if (errorScope === ErrorScope.Global) {
                            errorDialog.showError({ error: message });
                        }
                    }
                });
            } else if (networkError) {
                if (errorScope === ErrorScope.Global) {
                    errorDialog.showError({ error: networkError.message });
                }
            }
        }),
        setContext((operation, { headers = {} }) => {
            return {
                headers: {
                    ...headers,
                    authorization: `Bearer ${userContext.getUser().access_token}`,
                    "x-include-invisible-content": "1",
                },
            };
        }),
        createHttpLink({
            uri: `${config.API_URL}/graphql`,
        }),
    ]);

    const cache = new InMemoryCache({
        possibleTypes: fragmentTypes.possibleTypes,
        typePolicies: {},
    });

    const apolloClient = new ApolloClient({
        link,
        cache,
    });

    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default CustomApolloProvider;
